import React, { useState, useCallback, useEffect, useMemo } from "react";
import ofilter from "../../../helpers/ofilter";
import { labelCompare } from "../../../helpers/selectHelper";
import { useDispatch, useSelector } from "react-redux";
import {
    requestDestinations,
    requestSelectedLayoutObjects,
    requestLayoutList,
} from "../../../actions/exportLayoutActions2";
import {
    updateAttribute,
    ancestorHasInvalidFieldClassification,
    invalidateItemAndChildren,
    clearFlowExportDistributionPlatforms,
    clearFlowItemSVDedupes,
    clearFlowExportOfferMerges,
    clearFlowRelationParentLabels,
    clearFlowExportDestinationVariables,
    newFlowSVDedupe,
    deleteFlowSVDedupe,
    requestFlowLayoutErrors,
} from "../../../actions/flowActions";
import { requestLayoutFieldTree } from "../../../actions/fieldTreeActions";
import { GetDestinationExportIDs } from "../../../actions/DeploymentDestinationActions";
import { IDestinationExportColumn } from "../../../types/actions/companies";
import FlowItemDeployToggle from "./FlowExportItems/FlowItemDeployToggle";
import { showModal } from "../../../actions/actionCreators";
import { createSelector } from "reselect";
import { getFlowExportsByFlowItemId } from "../../../reducers/flowExports";
import { getFlowExportReportsArray } from "../../../reducers/flowExportReports";
import { getAncestorsFields } from "../../../reducers/flowRelations";
import { getSelectedFlowAndItemPermissions } from "../../../reducers/flowItems";

import FlowExportDistributionPlatforms from "./FlowExportItems/FlowExportDistributionPlatforms";
import FlowExportOfferEdit from "./FlowExportItems/FlowExportOfferEdit";
import FlowExportVariableEdit from "./FlowExportItems/FlowExportVariableEdit";
import { makeGetFlowSVDedupe } from "./FlowSVDedupeEdit";
import { requestClientVariableGroupings } from "../../../actions/clientVariableActions";
import { DeploySettings } from "../../../types/stores/companyTable";
import type { FlowExportsByItemId } from "../../../reducers/flowExports";
import { FormControl, Grid, Divider, Button, TextField, Icon, Tooltip } from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import TradedeskDeliveryTypes from "./FlowExportItems/TradedeskDeliveryTypes";
import PubmaticDeliveryTypes from "./FlowExportItems/PubmaticDeliveryTypes";
import ActivationDestinationSchedules from "./FlowExportItems/ActivationDestinationSchedules";
import FlowExportReportsTable from "./FlowExportItems/FlowExportReportsTable";
import RestrictedFieldsTable from "./FlowExportItems/RestrictedFieldsTable";
import { IAppState } from "../../../types/stores/index";
import { FlowExport, FlowExportReport, FlowItemFieldsUsed } from "../../../types/stores/flowTypes";
import { IAdvertisersByCompany } from "../../../types/stores/vars";
import ExportDestinationSquare from "./FlowExportItems/ExportDestinationSquare";
import FlowTaxonomyFieldEdit from "./FlowExportItems/FlowTaxonomyFieldEdit";
import SAATemplateTypes from "./FlowExportItems/SAATemplateTypes";
import { getUserCompanies } from "../../../actions/adminCompaniesActions";
import { anyParentFlowItemsWithFirstPartyOnly } from "../../../actions/flowActions";
import { getFlowRelationsForSelectedFlow } from "../../../reducers/flowRelations";
import { getFlowFiltersForSelectedFlow } from "../../../reducers/flowFilters";
import { getAncestorFilterFieldIdsForFlowItemId } from "../../../helpers/flowItems";
import {
    ShouldGrayOut,
    ShouldRequestMixed3pcExperian,
    ShouldRequestThirdpAccess,
} from "../../../helpers/exportsBussisnesRules";

type Props = {
    flowItemId: number;
    isModal: boolean;
    hideExtra: boolean;
};

const FlowExportEdit: React.FC<Props> = (props: Props) => {
    const dispatch = useDispatch();

    const [hasFieldRestrictions, setHasFieldRestrictions] = useState(false);
    const [fieldRestrictions, setFieldRestrictions] = useState<Array<FlowItemFieldsUsed>>([]);
    const [isLayoutChanged, setIsLayoutChanged] = useState(false);

    const getExportDestinations = makeGetExportDestinations();
    const getFlowExport = makeGetFlowExport();
    const getFlowExportReports = makeGetFlowExportReport();
    const getFlowSVDedupe = makeGetFlowSVDedupe();

    const PartnerList = useSelector((state: IAppState) => getExportDestinations(state));
    const destinationFieldRestrictions = useSelector((state: IAppState) => state.vars.destinationFieldRestrictions);
    const enabledFeatures = useSelector((state: IAppState) => state.session.enabledFeatures || []);
    const layouts = useSelector((state: IAppState) => state.layoutObjects.objects);
    const flowExport = useSelector((state: IAppState) => getFlowExport(state, props));
    const companyId = useSelector((state: IAppState) => state.session.companyId);
    const reports = useSelector((state: IAppState) => getFlowExportReports(state, props));
    const ancestorsFields = useSelector<IAppState, Array<FlowItemFieldsUsed>>(state => getAncestorsFields(state));
    const destinationDedupeSettings = useSelector((state: IAppState) => state.vars.destinationDedupeSettings);
    const flowSVDedupe = useSelector((state: IAppState) => getFlowSVDedupe(state, props));
    const environment = useSelector((state: IAppState) => state.session.environment);
    const isInternal = useSelector((state: IAppState) => state.session.isInternal);
    const flow = useSelector((state: IAppState) => state.flows.byId[state.selected.flow]);
    const canEdit = useSelector((state: IAppState) => getSelectedFlowAndItemPermissions(state).item.canEdit);
    const layoutFields = useSelector((state: IAppState) => state.layoutfields.byId);
    const layoutTypes = useSelector((state: IAppState) => state.vars.layoutTypes);
    const flowItemsById = useSelector((state: IAppState) => state.flowItems.byId);
    const variableGroups = useSelector((state: IAppState) => state.vars.variableGroupings || []);
    const destinationPartners = useSelector((state: IAppState) => state.vars.destinations);
    const loadingDestinations = useSelector((state: IAppState) => state.vars.loadingDestinations);
    const companyList = useSelector((state: IAppState) => state.adminCompanies.userCompanies || []);
    const fieldsById = useSelector((state: IAppState) => state.fields.byId || {}); // for mixed 3P + Experian detection
    const requestableDestinations = useSelector((state: IAppState) => state.vars.requestableDestinations);
    const flowItemLayoutErrors = useSelector((state: IAppState) => state.vars.flowItemLayoutErrors);
    const destinationExportIDColumns = useSelector(
        (state: IAppState) => state.vars.flowDestinationExportIDColumns || []
    );

    const isValidForIndirect = useMemo(
        () => dispatch(ancestorHasInvalidFieldClassification(flowExport?.FlowItemId)),
        [dispatch, flowExport?.FlowItemId]
    );

    const resetIsLayoutChanged = () => {
        setIsLayoutChanged(false);
    };

    const getCountType = (inputString: string): string => {
        const normalized = (inputString || "").trim().toUpperCase();
        if (normalized === "PERSONID" || normalized === "PID") {
            return "Person Count";
        } else if (normalized === "LIVINGUNITID") {
            return "HH Count";
        } else {
            return "Unique Count";
        }
    };

    const formatCountDisplay = (): string => {
        if (!props.isModal || !canShowCount() || !flowExport) {
            return "";
        }

        const quantity = flowItemsById[flowExport.FlowItemId].FlowItemQty;
        const formattedNumber = formatNumber(quantity);

        if (!enabledFeatures.includes("in-line-counts") || quantity <= 0) {
            return formattedNumber;
        }

        const destinationCompany = companyList.find(x => x.CompanyID === thisDestination?.item.PartnerAccessId);
        const countType = getCountType(destinationCompany?.SVClientKey ?? "");
        return `${formattedNumber} ${countType}`;
    };

    // flowItemsById[z.value].FlowItemQty
    const processLayoutChanged = useCallback(
        layoutId => {
            if (flowExport != null) {
                const layout = layouts.filter(x => x.Layout.LayoutID == layoutId)[0];

                //if the new layout is pivot, offer merges and sv dedupes are not needed any more
                if (layout && layout.Layout && layout.Layout.IsPivot) {
                    dispatch(clearFlowItemSVDedupes(flowExport.FlowItemId));
                    dispatch(clearFlowExportOfferMerges(flowExport.FlowItemId));
                } else if (layout && layout.Layout && !layout.Layout.IsPivot) {
                    //not pivot, clear the parent labels
                    dispatch(clearFlowRelationParentLabels(flowExport.FlowItemId));
                }

                dispatch(updateAttribute("flowExports", flowExport.FlowExportId, "ExportId", layoutId));
                dispatch(invalidateItemAndChildren(flowExport.FlowItemId));
                if (reports.length > 0) {
                    reports.forEach(x => {
                        dispatch(updateAttribute("flowExportReports", x.FlowExportReportId, "DimensionFieldKeys", ""));
                        dispatch(updateAttribute("flowExportReports", x.FlowExportReportId, "MeasureFieldKeys", ""));
                        dispatch(updateAttribute("flowExportReports", x.FlowExportReportId, "LayoutId", layoutId));
                    });
                }

                setIsLayoutChanged(true);
            }
        },
        [dispatch, flowExport, reports, layouts]
    );

    useEffect(() => {
        if (!variableGroups || variableGroups.length == 0) {
            dispatch(requestClientVariableGroupings());
        }
    }, [dispatch, variableGroups]);

    useEffect(() => {
        if (!companyList || companyList.length === 0) {
            dispatch(getUserCompanies());
        }
    }, [dispatch, companyList]);

    useEffect(() => {
        if (flowExport?.DestinationId && enabledFeatures.includes("pid-ingest")) {
            dispatch(GetDestinationExportIDs(flowExport.DestinationId));
        }
    }, [dispatch, flowExport?.DestinationId, enabledFeatures]);

    const checkFieldRestrictions = useCallback(
        destinationId => {
            if (
                !destinationFieldRestrictions ||
                destinationFieldRestrictions.length == 0 ||
                !ancestorsFields ||
                ancestorsFields.length == 0 ||
                destinationFieldRestrictions.filter(x => x.DestinationId == destinationId).length == 0
            ) {
                setHasFieldRestrictions(false);
                setFieldRestrictions([]);
                dispatch(updateAttribute("flowItems", props.flowItemId, "IsValid", true));
                return;
            }

            const restrictedIds = destinationFieldRestrictions
                .filter(x => x.DestinationId == destinationId)
                .map(x => x.FieldKey);

            const fieldRestrictions = ancestorsFields.filter(x => restrictedIds.includes(x.FieldId));

            if (fieldRestrictions.length > 0) {
                setHasFieldRestrictions(true);
                setFieldRestrictions(fieldRestrictions);
                dispatch(updateAttribute("flowItems", props.flowItemId, "IsValid", false));
            } else {
                setHasFieldRestrictions(false);
                setFieldRestrictions([]);
                dispatch(updateAttribute("flowItems", props.flowItemId, "IsValid", true));
            }
        },
        [ancestorsFields, destinationFieldRestrictions, props.flowItemId, dispatch]
    );

    useEffect(() => {
        if (
            !props.hideExtra &&
            !loadingDestinations &&
            (!PartnerList || PartnerList.length == 0 || !destinationFieldRestrictions)
        ) {
            dispatch(requestDestinations());
        }
    }, [dispatch, PartnerList, destinationFieldRestrictions, loadingDestinations, props]);

    useEffect(() => {
        if (!layoutFields) {
            dispatch(requestLayoutFieldTree());
        }
    }, [dispatch, layoutFields]);

    useEffect(() => {
        if (!layoutTypes) {
            dispatch(requestLayoutList("replace"));
        }
    }, [dispatch, layoutTypes]);

    useEffect(() => {
        if (!flowItemLayoutErrors) {
            dispatch(requestFlowLayoutErrors());
        }
    }, [dispatch, flowItemLayoutErrors]);

    useEffect(() => {
        //check if there is a new destination layout for
        const destinationId = flowExport ? flowExport.DestinationId : null;
        const exportId = flowExport ? flowExport.ExportId : null;
        const destinationLayout = (layouts || {}).find(
            x =>
                x.Layout.LayoutID != null &&
                x.Layout.LayoutCompanyID == destinationId &&
                x.Layout.IsActiveDestinationLayout
        );

        let destinationLayoutId = null;
        if (destinationLayout && destinationLayout.Layout) {
            destinationLayoutId = destinationLayout.Layout.LayoutID;
        }

        //if there is a destination layout for the selected destination
        //and is not the currently saved in the export, trigger the change
        if (destinationLayoutId && exportId != destinationLayoutId) {
            // Default is enforced when there is no layout assigned
            // OR the Destination Layout and Logged In Company are different
            //    AND "destination-company-layouts" is OFF
            // EMS-21681
            if (
                exportId == null ||
                (destinationLayout.Layout.LayoutCompanyID != companyId &&
                    !enabledFeatures.includes("destination-company-layouts"))
            ) {
                processLayoutChanged(destinationLayoutId);
            }
        }
    }, [flowExport, enabledFeatures, layouts, companyId, processLayoutChanged]);

    useEffect(() => {
        if (
            flowExport &&
            flowExport.DestinationId &&
            flowExport.DestinationId > 0 &&
            destinationFieldRestrictions &&
            destinationFieldRestrictions.filter(x => x.DestinationId == flowExport.DestinationId).length > 0
        ) {
            checkFieldRestrictions(flowExport.DestinationId);
        }
    }, [flowExport, destinationFieldRestrictions, checkFieldRestrictions]);

    useEffect(() => {
        if (
            enabledFeatures.includes("pid-ingest") &&
            flowExport?.DestinationId &&
            destinationExportIDColumns &&
            destinationExportIDColumns.length > 0 &&
            !flowExport.ExportIDColumn
        ) {
            setDefaultExportIDColumn(flowExport.DestinationId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [destinationExportIDColumns, flowExport?.DestinationId, flowExport?.ExportIDColumn, enabledFeatures]);

    const layoutChanged = (_, e) => processLayoutChanged(e && e.value ? e.value : null);

    const setDefaultExportIDColumn = (destinationId: number | undefined) => {
        const columns = (destinationExportIDColumns || []).filter(
            (x: IDestinationExportColumn) => x.CompanyID === destinationId
        );
        const defaultColumn = columns.find((x: IDestinationExportColumn) => x.IsDefault);

        dispatch(
            updateAttribute(
                "flowExports",
                flowExport?.FlowExportId,
                "ExportIDColumn",
                defaultColumn ? defaultColumn.ColumnName : null
            )
        );
    };

    const setDefaultDedupeSetting = (destinationId: number | undefined) => {
        let setting = destinationDedupeSettings.find(x => x.CompanyID == companyId && x.DestinationID == destinationId);

        // Id = 0 always contains the current company default settings.
        if (!setting) {
            setting = destinationDedupeSettings.find(x => x.Id == 0);
        }

        // the destination default is dedupe on.
        if (setting && setting.IsDedupeDefault) {
            // dedupe was off
            if (!flowSVDedupe) {
                dispatch(newFlowSVDedupe(flowExport?.FlowItemId, setting.DefaultDedupeKey, setting.DedupeSortByFields));
                dispatch(
                    updateAttribute(
                        "flowExports",
                        flowExport?.FlowExportId,
                        "CustomDedupe",
                        setting.IsCustomDedupeDefault
                    )
                );
            } else {
                const jsonSortByFields =
                    setting.DedupeSortByFields && setting.DedupeSortByFields.length > 0
                        ? JSON.parse(setting.DedupeSortByFields)
                        : [];
                dispatch(
                    updateAttribute("flowSVDedupes", flowSVDedupe.FlowSVDedupeId, "SVField", setting.DefaultDedupeKey)
                );
                dispatch(
                    updateAttribute("flowSVDedupes", flowSVDedupe.FlowSVDedupeId, "SortByFields", jsonSortByFields)
                );
                dispatch(
                    updateAttribute(
                        "flowExports",
                        flowExport?.FlowExportId,
                        "CustomDedupe",
                        setting.IsCustomDedupeDefault,
                        true
                    )
                );
            }
        } else if (flowSVDedupe) {
            dispatch(deleteFlowSVDedupe(flowSVDedupe.FlowSVDedupeId));
        }
    };

    const flowRelations = useSelector((state: IAppState) => getFlowRelationsForSelectedFlow(state));
    const flowFilters = useSelector((state: IAppState) => getFlowFiltersForSelectedFlow(state));
    const ancestorFilterFieldIds = getAncestorFilterFieldIdsForFlowItemId(
        props.flowItemId,
        Array.isArray(flowRelations) ? flowRelations : Object.values(flowRelations || {}),
        Array.isArray(flowFilters) ? flowFilters : Object.values(flowFilters || {})
    );

    const hasExperian = ancestorsFields.some(f => {
        const fieldObj: any = fieldsById[f.FieldId];
        return fieldObj && fieldObj.companyid === 0 && ancestorFilterFieldIds.includes(f.FieldId); // Company 0 denotes Experian data
    });
    const thirdPartyProviderCount = new Set(
        ancestorsFields
            .filter(x => x.IsThirdParty && ancestorFilterFieldIds.includes(x.FieldId))
            .map(x => x.FieldTableKey)
    ).size;
    const hasThirdParty = thirdPartyProviderCount > 0;
    const hasFirstParty = (ancestorsFields as Array<{ FieldClassification: string; FieldId: number }>).some(
        f => f.FieldClassification === "1P" && ancestorFilterFieldIds.includes(f.FieldId)
    );

    // Include explicit 3PC classification in mixed audience detection per EMS-19701
    const has3PCustom = ancestorsFields.some(
        f =>
            ((f as any).FieldClassification === "3PC" || (f as any).FieldClassification === "3PCMD") &&
            ancestorFilterFieldIds.includes(f.FieldId)
    );

    const isNotSelectableByMatrix = (dest: IAdvertisersByCompany) => {
        const trackingOn = !!dest.DestinationTrackingId;
        const marketplaceOn = !!dest.MarketplaceAccess;
        const exportOn = dest.AcceptFlag === "Y"; // adjust if your model differs
        const visibleOn = dest.CanExportDisplay ?? true; // adjust if your model differs
        const indirectDest = dest.IsIndirectDestination;

        const hasExperian = (ancestorsFields as Array<{ FieldId: number }>).some(f => {
            const fieldObj = fieldsById[f.FieldId];
            return fieldObj && fieldObj.companyid === 0 && ancestorFilterFieldIds.includes(f.FieldId); // Company 0 = Experian data
        });

        return ShouldGrayOut({
            hasExperian,
            has3P: hasThirdParty,
            has1P: hasFirstParty,
            has3PCustom,
            trackingOn,
            marketplaceOn,
            exportOn,
            visibleOn,
            indirectDest,
            isValidForIndirect,
        });
    };

    const setPartner = (newValue: number | undefined) => {
        const destination = (PartnerList || []).find(x => x.PartnerAccessId == newValue);
        const is1POnlyData = dispatch(anyParentFlowItemsWithFirstPartyOnly(flowExport?.FlowItemId));

        if (destination?.IsIndirectDestination && is1POnlyData) {
            showFirstPartyDataWarningModal();
            return; // Stop selection here
        }

        if (destination && isNotSelectableByMatrix(destination)) {
            const shouldRequestMixed3pcExperian = ShouldRequestMixed3pcExperian({
                hasExperian,
                has3P: hasThirdParty,
                has3PCustom,
                hasThirdParty,
                trackingOn: !!destination?.DestinationTrackingId,
                marketplaceOn: !!destination?.MarketplaceAccess,
                exportOn: destination?.AcceptFlag === "Y",
                visibleOn: destination?.CanExportDisplay ?? true,
            });

            const shouldRequestThirdpAccess = ShouldRequestThirdpAccess({
                hasExperian,
                has3P: hasThirdParty,
                has1P: hasFirstParty,
                has3PCustom,
                trackingOn: !!destination?.DestinationTrackingId,
                marketplaceOn: !!destination?.MarketplaceAccess,
                exportOn: destination?.AcceptFlag === "Y",
                visibleOn: destination?.CanExportDisplay ?? true,
                isRequestable:
                    requestableDestinations?.some(x => x.PartnerAccessId == destination?.PartnerAccessId) || false,
            });

            // If destination is indirect and is not valid, stop functionality before selection.
            if (destination?.IsIndirectDestination && !isValidForIndirect) {
                let trackingOn = !!destination?.DestinationTrackingId;
                if (hasExperian && (has3PCustom || hasFirstParty) && !hasThirdParty && !trackingOn) {
                    dispatch(
                        showModal("REQUEST_MIXED3P_EXPERIAN_ACCESS", { destinationName: destination?.CompanyName })
                    );
                    return;
                } else {
                    showInvalidIndirectDestinationWarningModal();
                    return; // Stop selection here
                }
            }

            if (shouldRequestMixed3pcExperian) {
                dispatch(showModal("REQUEST_MIXED3P_EXPERIAN_ACCESS", { destinationName: destination?.CompanyName }));
                return; // Stop selection here
            }

            // 3P only gating when destination lacks marketplace or tracking access
            if (shouldRequestThirdpAccess) {
                dispatch(showModal("REQUEST_THIRDP_ACCESS"));
                return; // Stop selection here
            }
        }

        // Updating the export to the default destination dedupe settings.
        setDefaultDedupeSetting(newValue);

        if (flowExport != null) {
            dispatch(clearFlowExportDestinationVariables(flowExport.FlowItemId, flowExport.DestinationId));
            dispatch(clearFlowExportDistributionPlatforms(flowExport.FlowItemId));

            dispatch(updateAttribute("flowExports", flowExport.FlowExportId, "DestinationId", newValue));
            // Reset DeliveryTypeId
            dispatch(updateAttribute("flowExports", flowExport.FlowExportId, "DeliveryTypeId", null));
            dispatch(updateAttribute("flowExports", flowExport.FlowExportId, "ActivationChannelId", null));

            // set destination layout
            if (destination && destination.DestinationLayoutId) {
                //begin flow-export-pivot-labels
                dispatch(clearFlowRelationParentLabels(flowExport.FlowItemId));
                //end flow-export-pivot-labels

                dispatch(
                    updateAttribute("flowExports", flowExport.FlowExportId, "ExportId", destination.DestinationLayoutId)
                );
                setIsLayoutChanged(true);

                dispatch(invalidateItemAndChildren(flowExport.FlowItemId));
                if (reports.length > 0) {
                    reports.forEach(x => {
                        dispatch(updateAttribute("flowExportReports", x.FlowExportReportId, "DimensionFieldKeys", ""));
                        dispatch(updateAttribute("flowExportReports", x.FlowExportReportId, "MeasureFieldKeys", ""));
                        dispatch(
                            updateAttribute(
                                "flowExportReports",
                                x.FlowExportReportId,
                                "LayoutId",
                                destination.DestinationLayoutId
                            )
                        );
                    });
                }
            } else {
                //If layout saved was a dif company destinationid and the user changes destinations, clear out the selected layout
                const theseLayouts = ofilter(
                    v => v["Layout"]["LayoutID"] != null && v["Layout"]["LayoutID"] == flowExport.ExportId,
                    layouts || {}
                );
                const selectedLayout = Object.keys(theseLayouts).map(item => ({
                    item: layouts[item],
                }));
                if (selectedLayout.length > 0 && selectedLayout[0].item.Layout.LayoutCompanyID != companyId) {
                    dispatch(updateAttribute("flowExports", flowExport.FlowExportId, "ExportId", null));

                    //begin flow-export-pivot-labels
                    //clear any pivot parent labels
                    dispatch(clearFlowRelationParentLabels(flowExport.FlowItemId));
                    //end flow-export-pivot-labels

                    //I may just delete the reports in this scenario, not sure yet
                    if (reports.length > 0) {
                        reports.forEach(x => {
                            dispatch(
                                updateAttribute("flowExportReports", x.FlowExportReportId, "DimensionFieldKeys", "")
                            );
                            dispatch(
                                updateAttribute("flowExportReports", x.FlowExportReportId, "MeasureFieldKeys", "")
                            );
                            dispatch(updateAttribute("flowExportReports", x.FlowExportReportId, "LayoutId", null));
                        });
                    }
                }
            }

            // Check if destination has field restrictions
            if (newValue && destinationFieldRestrictions) {
                const destinationId = newValue;
                checkFieldRestrictions(destinationId);
            } else {
                setHasFieldRestrictions(false);
                setFieldRestrictions([]);
                dispatch(updateAttribute("flowItems", props.flowItemId, "IsValid", true));
            }
        }
    };

    const showInvalidIndirectDestinationWarningModal = () => {
        dispatch(
            showModal("FILE_BIG_SIZE_UPLOAD_WARNING", {
                continue: () => {},
                type: "Validation Indirect Destination",
            })
        );
    };

    const showFirstPartyDataWarningModal = () => {
        dispatch(
            showModal("FILE_BIG_SIZE_UPLOAD_WARNING", {
                continue: () => {},
                type: "First Party Data to Indirect Destination",
            })
        );
    };

    const showLayoutModal = () => {
        const layout = layouts.filter(x => x.Layout.LayoutID == flowExport?.ExportId)[0];
        dispatch(requestSelectedLayoutObjects(layout.Layout.LayoutID));
        dispatch(showModal("FLOW_EXPORT_LAYOUT", layout));
    };

    const canShowCount = () => {
        if (flowExport) {
            let dest = flowExport.DestinationId;
            if (destinationPartners) {
                const partners = destinationPartners.filter(d => d.PartnerAccessId == dest);
                return partners[0].CanShowCount;
            } else {
                return true;
            }
        } else {
            return true;
        }
    };

    const isDestinationCompanyLayouts = enabledFeatures.includes("destination-company-layouts");

    // If selected destination has IsActiveDestinationLayout
    // EMS-21681 removed condition for LayoutCompanyID != companyId
    const selectedLayout = ofilter(
        v =>
            v["Layout"]["LayoutID"] != null &&
            v["Layout"]["LayoutCompanyID"] == (flowExport != null ? flowExport.DestinationId : null) &&
            (isDestinationCompanyLayouts || v["Layout"]["IsActiveDestinationLayout"] == true),
        layouts || {}
    );

    const destinationLayout = isDestinationCompanyLayouts
        ? []
        : Object.keys(selectedLayout)
              .map(item => ({
                  label: layouts[item].Layout.LayoutName,
                  value: layouts[item].Layout.LayoutID,
                  item: layouts[item],
              }))
              .sort(labelCompare);

    let theseLayouts = ofilter(
        v =>
            v["Layout"]["LayoutID"] != null &&
            v["Layout"]["LayoutCompanyID"] == companyId &&
            v["Layout"]["LayoutActive"] == "Y",
        layouts || {}
    );

    if (flowExport?.DestinationId && PartnerList && PartnerList.length > 0 && isDestinationCompanyLayouts) {
        const destination = PartnerList.filter(x => x.PartnerAccessId == flowExport.DestinationId);
        if (destination.length == 1 && destination[0].CompanyIsDestination) {
            theseLayouts = ofilter(
                v =>
                    v["Layout"]["LayoutID"] != null &&
                    v["Layout"]["LayoutCompanyID"] == flowExport?.DestinationId &&
                    v["Layout"]["LayoutActive"] == "Y",
                layouts || {}
            );
        }
    }

    let layoutList = Object.keys(theseLayouts)
        .map(item => ({
            label: layouts[item].Layout.LayoutName,
            value: layouts[item].Layout.LayoutID,
            item: layouts[item],
        }))
        .sort((a, b) => {
            if (a.item.Layout.IsActiveDestinationLayout && !b.item.Layout.IsActiveDestinationLayout) {
                return -1;
            } else if (!a.item.Layout.IsActiveDestinationLayout && b.item.Layout.IsActiveDestinationLayout) {
                return 1;
            }

            // Else go to the 2nd criteria
            if (a.label.toUpperCase() < b.label.toUpperCase()) {
                return -1;
            } else if (a.label.toUpperCase() > b.label.toUpperCase()) {
                return 1;
            } else {
                // nothing to split them
                return 0;
            }
        });

    let filteredDestinations = PartnerList || [];
    //Only auprod and ukprod can see the grouped partners, others only the parent partners
    if (environment != "auprod" && environment != "ukprod" && PartnerList && PartnerList.length > 0) {
        filteredDestinations = PartnerList.filter(x => !x.isGrouped || x.PartnerAccessId == x.GroupCompanyID);
    }

    // #EMS-8349
    const isAllow3p = enabledFeatures.includes("allow-multiple-3P-data-providers");
    const thirdPartyFields = ancestorsFields.filter(
        field => field.IsThirdParty === true && ancestorFilterFieldIds.includes(field.FieldId)
    );
    const uniqueFieldTableKeys = new Set(thirdPartyFields.map(field => field.FieldTableKey));
    const hasMoreThanOneUniqueFieldTableKey = uniqueFieldTableKeys.size > 1;

    // Helper function to determine if a destination should be disabled
    const isDestinationDisabled = (item: any) => {
        if (isAllow3p && hasMoreThanOneUniqueFieldTableKey) {
            // Existing multi 3P rule
            return !item.DestinationTrackingId;
        }
        return false;
    };

    const destinations = filteredDestinations
        .map(item => {
            const isNotSelectable = isNotSelectableByMatrix(item);

            return {
                label:
                    item.VerticalName +
                    " - " +
                    item.CompanyName +
                    (isInternal && item.FTPFolder != null ? " (" + item.FTPFolder + ")" : ""),
                value: item.PartnerAccessId,
                disabled: isDestinationDisabled(item),
                isNotSelectable,
                item,
            };
        })
        .sort(labelCompare);

    const layout = layouts && layouts.filter(x => x.Layout.LayoutID == flowExport?.ExportId)[0];
    const toArray = (objs: any) => (Array.isArray(objs) ? objs : objs ? [objs] : []);
    const hasField = (id: string | number, fields: Record<string | number, unknown>) => {
        const key = typeof id === "number" ? id : String(id);
        return fields != null && Object.prototype.hasOwnProperty.call(fields, key);
    };

    const filteredLayouts = destinationLayout.length > 0 ? destinationLayout : layoutList;
    const filteredLayout = filteredLayouts.filter(x => x.value == flowExport?.ExportId);
    if (filteredLayout.length == 0) {
        layoutList = layoutList.filter(opt => {
            const arr = toArray(opt?.item?.LayoutObjects);
            const type1 = arr.filter(o => o && o.ObjectType === 1);
            if (type1.length === 0) return true;
            return (
                type1.length > 0 && type1.every(o => o.ObjectId != null && hasField(o.ObjectId as any, layoutFields))
            );
        });
    }

    let hasLayoutError = false;
    if (
        flowItemLayoutErrors != null &&
        flowExport != null &&
        flowExport.FlowItemId != null &&
        flowExport.ExportId != null
    ) {
        if (
            flowItemLayoutErrors.filter(x => x.FlowItemId == flowExport.FlowItemId && x.LayoutId == flowExport.ExportId)
                .length > 0
        ) {
            hasLayoutError = true;
        }
    }

    const thisDestination = destinations.find(x => x.value == flowExport?.DestinationId);

    const deploySetting = thisDestination && thisDestination.item.DeploySetting;
    const saaTemplateDeploySetting = thisDestination && thisDestination.item.SAADeploySetting;
    const hasSnowflake = enabledFeatures.includes("snowflake-db");
    const hasFlowExportTemplateFields = enabledFeatures.includes("flow-export-template-fields");
    const isAdditionalTemplate =
        deploySetting == DeploySettings.DeployAdditionalFacebookTemplate && hasFlowExportTemplateFields;
    const isTradeDeskTemplate = deploySetting == DeploySettings.DeployTradedeskTemplate;
    const isPubmaticTemplate = deploySetting == DeploySettings.DeployPubmaticTemplate;
    const isPinterestTemplate = deploySetting == DeploySettings.DeployPinterestAutomationTemplate;
    const isFreewheelDriverFile = deploySetting == DeploySettings.DeployFreewheelDriverFile;
    const isMagniteDriverFile = deploySetting == DeploySettings.DeployMagniteDriverFile;
    const isTikTokTemplate = deploySetting == DeploySettings.DeployTikTokDriverFile;
    const isSnowflakeTable = deploySetting == DeploySettings.DeploySnowflakeTable && hasSnowflake;
    const isNonExternalFields = deploySetting == DeploySettings.DeployNonExternalFields;
    let splitOverwrite = false;
    if (
        isAdditionalTemplate ||
        isPinterestTemplate ||
        isTikTokTemplate ||
        isTradeDeskTemplate ||
        isPubmaticTemplate ||
        isFreewheelDriverFile ||
        isMagniteDriverFile ||
        isSnowflakeTable ||
        isNonExternalFields
    ) {
        splitOverwrite = false;
    } else if (layout != null) {
        splitOverwrite = layout.Layout.DeploySeparateFiles;
    }

    const isActivationDestination = Boolean(
        thisDestination &&
            filteredDestinations.find(x => thisDestination.value == x.PartnerAccessId)?.CompanyIsActivationDestination
    );

    const destinationId = flowExport?.DestinationId;
    let showRequestExportApprovalMessage = false;
    if (
        destinationId &&
        filteredDestinations &&
        filteredDestinations.filter(x => x.PartnerAccessId == destinationId && x.EnforceDeploymentRequest).length > 0
    ) {
        showRequestExportApprovalMessage = true;
    }

    const formatNumber = (val: number) => (val != null ? val.toLocaleString() : "");

    return flowExport ? (
        <div style={{ paddingBottom: "35px" }}>
            {showRequestExportApprovalMessage &&
                (flow && flow.FlowBaseType > 0 ? (
                    <div>Your chosen destination will require you to request export approval.</div>
                ) : (
                    <div>
                        Your chosen destination will require you to request export approval. To do so, click the 3 dots
                        menu in the upper right hand corner from the main flow canvas and select &quot;Request Export
                        Approval&quot;
                    </div>
                ))}
            <div style={{ display: "flex", justifyContent: "space-between" }}>
                <div className="flow-edit-header" style={{ width: "33%", display: "flex", justifyContent: "start" }}>
                    <div style={{ marginTop: "20px" }}>
                        <p>Export Settings</p>
                    </div>
                    {!enabledFeatures.includes("activation-receipt-deployment") && (
                        <div className="flow-edit-header-actions">
                            <FlowItemDeployToggle
                                flowItemId={flowExport.FlowItemId}
                                disabled={!canEdit || (flow && flow.IsLocked)}
                            />
                        </div>
                    )}
                </div>
                <div className="flow-edit-header" style={{ fontSize: "24px", textAlign: "center" }}>
                    {formatCountDisplay()}
                </div>
                <div style={{ width: "33%" }}>&nbsp;</div>
            </div>
            <Divider style={{ margin: "20px auto" }} />
            <div className="flow-edit-body">
                <Grid container justifyContent="center">
                    <Grid container spacing={2} justifyContent="center">
                        {enabledFeatures.includes("flow-export-destination-selection-new-ui") && thisDestination ? (
                            <Grid item xs={12}>
                                <div style={{ display: "flex", justifyContent: "center" }}>
                                    <ExportDestinationSquare
                                        flowItemId={props.flowItemId}
                                        destination={thisDestination?.item}
                                        selectedDestinationId={flowExport?.DestinationId}
                                        onClick={() => setPartner(undefined)}
                                    />
                                </div>
                            </Grid>
                        ) : (
                            <Grid item xs={8}>
                                <FormControl fullWidth required>
                                    <Autocomplete
                                        id="destinationSelectionLabel"
                                        options={destinations}
                                        getOptionLabel={option => (option && option.label) || ""}
                                        getOptionSelected={(option, value) => value.value === option.value}
                                        onChange={(_, e) => {
                                            if (e && !e.disabled) {
                                                setPartner(e.value);
                                            }
                                        }}
                                        renderOption={option => (
                                            <span
                                                style={{
                                                    color: option.isNotSelectable
                                                        ? "#888" // gray out when mixed audience & no TrackingID
                                                        : "inherit",
                                                }}
                                            >
                                                {option.label}
                                            </span>
                                        )}
                                        autoComplete
                                        value={thisDestination || null}
                                        disabled={PartnerList == null || !canEdit || (flow && flow.IsLocked)}
                                        renderInput={params => (
                                            <TextField {...params} label="Export Destination" fullWidth />
                                        )}
                                        getOptionDisabled={option => option.disabled}
                                    />
                                </FormControl>
                            </Grid>
                        )}

                        {saaTemplateDeploySetting != null && saaTemplateDeploySetting != 0 && (
                            <Grid item xs={8}>
                                <FormControl fullWidth required>
                                    <SAATemplateTypes
                                        flowExport={flowExport}
                                        canEdit={canEdit}
                                        saaTemplateDeploySetting={saaTemplateDeploySetting?.toString()}
                                    />
                                </FormControl>
                            </Grid>
                        )}

                        {(deploySetting == DeploySettings.DeployTradedeskTemplate ||
                            deploySetting == DeploySettings.DeployXandrTemplate) && (
                            <Grid item xs={8}>
                                <TradedeskDeliveryTypes
                                    flowExport={flowExport}
                                    canEdit={canEdit}
                                    deploySetting={deploySetting}
                                />
                            </Grid>
                        )}

                        {deploySetting == DeploySettings.DeployPubmaticTemplate && (
                            <Grid item xs={8}>
                                <PubmaticDeliveryTypes
                                    flowExport={flowExport}
                                    canEdit={canEdit}
                                    deploySetting={deploySetting}
                                />
                            </Grid>
                        )}
                        {isActivationDestination && (
                            <Grid item xs={8}>
                                <ActivationDestinationSchedules
                                    flowExport={flowExport}
                                    destination={thisDestination?.item as IAdvertisersByCompany}
                                    canEdit={canEdit}
                                />
                            </Grid>
                        )}
                        {enabledFeatures.includes("pid-ingest") &&
                            flowExport?.DestinationId &&
                            destinationExportIDColumns &&
                            destinationExportIDColumns.length > 0 && (
                                <Grid item xs={8}>
                                    <FormControl fullWidth required>
                                        <Autocomplete
                                            id="exportIdColumnSelectionLabel"
                                            options={(destinationExportIDColumns || [])
                                                .filter(
                                                    (x: IDestinationExportColumn) =>
                                                        x.CompanyID === flowExport.DestinationId
                                                )
                                                .map((x: IDestinationExportColumn) => ({
                                                    label: x.ColumnName,
                                                    value: x.ColumnName,
                                                }))
                                                .sort(labelCompare)}
                                            getOptionLabel={option => option.label}
                                            getOptionSelected={(option, value) => value.value === option.value}
                                            onChange={(_, e) => {
                                                dispatch(
                                                    updateAttribute(
                                                        "flowExports",
                                                        flowExport.FlowExportId,
                                                        "ExportIDColumn",
                                                        e ? e.value : null
                                                    )
                                                );
                                            }}
                                            autoComplete
                                            value={
                                                flowExport.ExportIDColumn
                                                    ? {
                                                          label: flowExport.ExportIDColumn,
                                                          value: flowExport.ExportIDColumn,
                                                      }
                                                    : null
                                            }
                                            disabled={!canEdit || (flow && flow.IsLocked)}
                                            renderInput={params => (
                                                <TextField {...params} label="Export ID Type" fullWidth />
                                            )}
                                        />
                                    </FormControl>
                                </Grid>
                            )}
                        <Grid item xs={8}>
                            <div style={{ display: "flex", paddingBottom: "20px" }}>
                                <FormControl fullWidth required>
                                    <Autocomplete
                                        id="layoutSelectionLabel"
                                        options={destinationLayout.length > 0 ? destinationLayout : layoutList}
                                        getOptionLabel={option => option.label}
                                        getOptionSelected={(option, value) => value.value === option.value}
                                        onChange={layoutChanged}
                                        autoComplete
                                        value={filteredLayout.length > 0 ? filteredLayout[0] : null}
                                        disabled={
                                            destinationLayout.length > 0
                                                ? true
                                                : !canEdit || (flow && flow.IsLocked)
                                                  ? true
                                                  : false
                                        }
                                        renderOption={option => (
                                            <Tooltip
                                                title={
                                                    option.item.Layout.IsActiveDestinationLayout ? "Active" : "Inactive"
                                                }
                                            >
                                                <span>{option.label}</span>
                                            </Tooltip>
                                        )}
                                        renderInput={params => <TextField {...params} label="Layouts" fullWidth />}
                                    />
                                </FormControl>
                                <Button
                                    style={{ fontSize: 12 }}
                                    variant="contained"
                                    color="secondary"
                                    className={"edit-button"}
                                    onClick={showLayoutModal}
                                    disabled={
                                        !flowExport?.ExportId || !canEdit || (flow && flow.IsLocked) || hasLayoutError
                                    }
                                >
                                    View Details
                                </Button>
                            </div>
                        </Grid>
                    </Grid>
                </Grid>
                {hasFieldRestrictions ? (
                    <div style={{ width: "100%" }}>
                        <div className="alert alert-warning-original flow-item-warnings">
                            <Icon style={{ marginRight: "10px", position: "relative", top: "2px" }}>error_outline</Icon>
                            The selected destination does not allow the use of the below fields
                        </div>
                        <RestrictedFieldsTable fieldRestrictions={fieldRestrictions} />
                    </div>
                ) : (
                    <div>
                        {layout &&
                            layout.Layout &&
                            layout.LayoutTaxonomyObjects != null &&
                            layout.LayoutTaxonomyObjects.length > 0 &&
                            !props.hideExtra && <FlowTaxonomyFieldEdit flowItemId={flowExport?.FlowItemId} />}

                        <FlowExportVariableEdit
                            flowItemId={flowExport?.FlowItemId}
                            destinationId={flowExport?.DestinationId}
                            isFreewheelOrMagniteDriverFile={isFreewheelDriverFile || isMagniteDriverFile}
                            variableGroups={variableGroups}
                        />

                        {flowExport && flowExport.DestinationId && (
                            <FlowExportDistributionPlatforms
                                destinationId={flowExport.DestinationId}
                                flowItemId={flowExport.FlowItemId}
                            />
                        )}
                        {/* <div hidden={props.isModal}> */}
                        {layout && layout.Layout && !props.hideExtra && (
                            <FlowExportOfferEdit
                                resetIsLayoutChanged={resetIsLayoutChanged}
                                destinationId={flowExport?.DestinationId}
                                deliveryTypeId={flowExport?.DeliveryTypeId}
                                splitOverwrite={splitOverwrite}
                                flowItemId={flowExport?.FlowItemId}
                                flowExportId={flowExport?.FlowItemId}
                                layoutId={flowExport?.ExportId}
                                isAdditionalTemplate={isAdditionalTemplate}
                                isPinterestTemplate={isPinterestTemplate}
                                isTradeDeskTemplate={isTradeDeskTemplate}
                                isPubmaticTemplate={isPubmaticTemplate}
                                isFreewheelDriverFile={isFreewheelDriverFile}
                                isMagniteDriverFile={isMagniteDriverFile}
                                isTikTokTemplate={isTikTokTemplate}
                                isSnowflakeTable={isSnowflakeTable}
                                isSAAFileTemplate={saaTemplateDeploySetting != 0 && saaTemplateDeploySetting != null}
                                isMultiExport={props.isModal}
                                deploySetting={deploySetting}
                                isLayoutChanged={isLayoutChanged}
                                activationChannelId={flowExport?.ActivationChannelId}
                            />
                        )}

                        <FlowExportReportsTable flowExport={flowExport} canEdit={canEdit} reports={reports} />
                    </div>
                )}
            </div>
        </div>
    ) : null;
};

type FlowExportSelector<S> = (state: IAppState, props: Props) => S;
export const makeGetFlowExport = (): FlowExportSelector<FlowExport | undefined> =>
    // @ts-ignore
    createSelector(
        (state: IAppState) => getFlowExportsByFlowItemId(state),
        (_, props: Props) => props.flowItemId,
        (flowExportsByFlowItemId: FlowExportsByItemId, flowItemId: number): FlowExport | undefined =>
            flowExportsByFlowItemId[flowItemId]
    );

type FlowExportReportSelector<S> = (state: IAppState, props: Props) => S;
const makeGetFlowExportReport = (): FlowExportReportSelector<Array<FlowExportReport>> =>
    // @ts-ignore
    createSelector(
        (state: IAppState) => getFlowExportReportsArray(state),
        (_, props: Props) => props.flowItemId,
        (reports: Array<FlowExportReport>, flowItemId: number): Array<FlowExportReport> =>
            reports.filter(x => x.ParentFlowItemId == flowItemId)
    );

type FlowExportDestinationSelector<S> = (state: IAppState) => S;
export const makeGetExportDestinations = (): FlowExportDestinationSelector<Array<IAdvertisersByCompany>> =>
    // @ts-ignore
    createSelector(
        (state: IAppState) => state.vars.destinations,
        (destinations: Array<IAdvertisersByCompany>): Array<IAdvertisersByCompany> =>
            destinations ? destinations.filter(x => x.AcceptFlag == "Y") : []
    );

export default FlowExportEdit;
